import React, { useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'

const ADMIN_EMAIL = 'ah.harish@gmail.com'

export default function AdminReport({ session, onClose }) {
  const [entries, setEntries] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetch = async () => {
      const { data } = await supabase
        .from('entries')
        .select('*, profiles(full_name, email)')
        .order('created_at', { ascending: true })
      setEntries(data || [])
      setLoading(false)
    }
    fetch()
  }, [])

  if (session.user.email !== ADMIN_EMAIL) return null

  // ── Metrics ──
  const total = entries.length

  // Per user
  const userMap = {}
  entries.forEach(e => {
    const name = e.profiles?.full_name || e.profiles?.email || 'Unknown'
    userMap[name] = (userMap[name] || 0) + 1
  })
  const users = Object.entries(userMap).sort((a, b) => b[1] - a[1])

  // Per category
  const catMap = {}
  entries.forEach(e => { catMap[e.category] = (catMap[e.category] || 0) + 1 })
  const cats = Object.entries(catMap).sort((a, b) => b[1] - a[1])
  const maxCat = cats[0]?.[1] || 1

  // Per source type
  const srcMap = {}
  entries.forEach(e => { srcMap[e.source_type] = (srcMap[e.source_type] || 0) + 1 })
  const sources = Object.entries(srcMap).sort((a, b) => b[1] - a[1])

  // Weekly trend — last 8 weeks
  const now = new Date()
  const weeks = Array.from({ length: 8 }, (_, i) => {
    const start = new Date(now)
    start.setDate(now.getDate() - (7 * (7 - i)) - now.getDay())
    const end = new Date(start)
    end.setDate(start.getDate() + 6)
    const count = entries.filter(e => {
      const d = new Date(e.created_at)
      return d >= start && d <= end
    }).length
    const label = `${start.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}`
    return { label, count }
  })
  const maxWeek = Math.max(...weeks.map(w => w.count), 1)

  // Tags
  const tagMap = {}
  entries.forEach(e => (e.tags || []).forEach(t => { tagMap[t] = (tagMap[t] || 0) + 1 }))
  const topTags = Object.entries(tagMap).sort((a, b) => b[1] - a[1]).slice(0, 12)

  // This week vs last week
  const thisWeekStart = new Date(now)
  thisWeekStart.setDate(now.getDate() - now.getDay())
  thisWeekStart.setHours(0, 0, 0, 0)
  const lastWeekStart = new Date(thisWeekStart)
  lastWeekStart.setDate(thisWeekStart.getDate() - 7)
  const thisWeek = entries.filter(e => new Date(e.created_at) >= thisWeekStart).length
  const lastWeek = entries.filter(e => {
    const d = new Date(e.created_at)
    return d >= lastWeekStart && d < thisWeekStart
  }).length

  // Most recent entry
  const latest = entries[entries.length - 1]

  const s = {
    overlay: {
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
      backdropFilter: 'blur(6px)', zIndex: 300, overflowY: 'auto',
      display: 'flex', justifyContent: 'center', padding: '40px 20px'
    },
    panel: {
      background: 'var(--bg-modal)', border: '1px solid var(--border-strong)',
      borderRadius: 16, padding: '40px 44px', width: '100%', maxWidth: 900,
      fontFamily: 'Outfit, sans-serif', color: 'var(--text-primary)', position: 'relative',
      height: 'fit-content'
    },
    header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 32 },
    title: { fontFamily: 'Fraunces, serif', fontSize: 28, fontWeight: 300, color: 'var(--text-primary)' },
    subtitle: { fontFamily: 'DM Mono, monospace', fontSize: 9, letterSpacing: '0.12em', color: 'var(--text-tertiary)', textTransform: 'uppercase', marginTop: 4 },
    closeBtn: { background: 'none', border: '1px solid var(--border-strong)', borderRadius: 6, color: 'var(--text-tertiary)', cursor: 'pointer', padding: '6px 14px', fontSize: 12, fontFamily: 'DM Mono, monospace' },
    grid: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 32 },
    statCard: { background: 'var(--bg-card)', border: '1px solid var(--border-mid)', borderRadius: 10, padding: '18px 20px' },
    statVal: { fontFamily: 'Fraunces, serif', fontSize: 36, fontWeight: 300, color: 'var(--accent)', lineHeight: 1 },
    statLabel: { fontFamily: 'DM Mono, monospace', fontSize: 9, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--text-tertiary)', marginTop: 6 },
    statSub: { fontSize: 11, color: 'var(--text-secondary)', marginTop: 4 },
    section: { marginBottom: 32 },
    sectionTitle: { fontFamily: 'DM Mono, monospace', fontSize: 9, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'var(--text-tertiary)', marginBottom: 16, paddingBottom: 8, borderBottom: '1px solid var(--border-subtle)' },
    row: { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 },
    barLabel: { width: 160, fontSize: 12, color: 'var(--text-secondary)', flexShrink: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
    barTrack: { flex: 1, height: 4, background: 'var(--border-mid)', borderRadius: 2, position: 'relative' },
    barFill: (pct) => ({ position: 'absolute', left: 0, top: 0, height: '100%', width: `${pct}%`, background: 'var(--accent)', borderRadius: 2, opacity: 0.8 }),
    barCount: { width: 28, textAlign: 'right', fontFamily: 'DM Mono, monospace', fontSize: 10, color: 'var(--accent-soft)', flexShrink: 0 },
    weekGrid: { display: 'flex', alignItems: 'flex-end', gap: 8, height: 80 },
    weekBar: (h) => ({ flex: 1, background: 'var(--accent)', borderRadius: '3px 3px 0 0', height: `${h}%`, minHeight: 3, opacity: 0.75, transition: 'height 0.3s' }),
    weekLabel: { fontFamily: 'DM Mono, monospace', fontSize: 8, color: 'var(--text-tertiary)', textAlign: 'center', marginTop: 6 },
    tagCloud: { display: 'flex', flexWrap: 'wrap', gap: 8 },
    tag: { background: 'var(--tag-bg)', border: '1px solid var(--tag-border)', borderRadius: 3, color: 'var(--tag-color)', fontFamily: 'DM Mono, monospace', fontSize: 9, letterSpacing: '0.04em', padding: '3px 10px', textTransform: 'uppercase' },
    userRow: { display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0', borderBottom: '1px solid var(--border-subtle)' },
    avatar: { width: 32, height: 32, borderRadius: '50%', background: 'var(--accent-muted)', border: '1px solid var(--border-accent)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--accent-soft)', fontSize: 12, fontWeight: 600, flexShrink: 0 },
    twoCol: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 },
  }

  if (loading) return (
    <div style={s.overlay}>
      <div style={{ ...s.panel, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 200 }}>
        <div className="loading-spinner" />
      </div>
    </div>
  )

  return (
    <div style={s.overlay} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={s.panel}>
        <div style={s.header}>
          <div>
            <div style={s.title}>Admin Report</div>
            <div style={s.subtitle}>K Base · {new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })} · Visible only to you</div>
          </div>
          <button style={s.closeBtn} onClick={onClose}>Close ✕</button>
        </div>

        {/* Top stats */}
        <div style={s.grid}>
          <div style={s.statCard}>
            <div style={s.statVal}>{total}</div>
            <div style={s.statLabel}>Total Entries</div>
          </div>
          <div style={s.statCard}>
            <div style={s.statVal}>{users.length}</div>
            <div style={s.statLabel}>Active Users</div>
          </div>
          <div style={s.statCard}>
            <div style={s.statVal}>{thisWeek}</div>
            <div style={s.statLabel}>This Week</div>
            <div style={s.statSub}>{lastWeek} last week</div>
          </div>
          <div style={s.statCard}>
            <div style={s.statVal}>{cats.length}</div>
            <div style={s.statLabel}>Categories Used</div>
            {latest && <div style={s.statSub}>Latest: {new Date(latest.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}</div>}
          </div>
        </div>

        {/* Weekly trend */}
        <div style={s.section}>
          <div style={s.sectionTitle}>Weekly Activity — Last 8 Weeks</div>
          <div style={s.weekGrid}>
            {weeks.map((w, i) => (
              <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end', height: '100%' }}>
                <div style={s.weekBar(maxWeek > 0 ? (w.count / maxWeek) * 100 : 0)} title={`${w.count} entries`} />
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 2 }}>
            {weeks.map((w, i) => (
              <div key={i} style={{ flex: 1, ...s.weekLabel }}>{w.label}</div>
            ))}
          </div>
        </div>

        <div style={s.twoCol}>
          {/* Categories */}
          <div style={s.section}>
            <div style={s.sectionTitle}>By Category</div>
            {cats.map(([cat, count]) => (
              <div key={cat} style={s.row}>
                <span style={s.barLabel}>{cat}</span>
                <div style={s.barTrack}>
                  <div style={s.barFill((count / maxCat) * 100)} />
                </div>
                <span style={s.barCount}>{count}</span>
              </div>
            ))}
          </div>

          {/* Source types */}
          <div style={s.section}>
            <div style={s.sectionTitle}>By Source Type</div>
            {sources.map(([src, count]) => (
              <div key={src} style={s.row}>
                <span style={s.barLabel}>{src}</span>
                <div style={s.barTrack}>
                  <div style={s.barFill((count / (sources[0]?.[1] || 1)) * 100)} />
                </div>
                <span style={s.barCount}>{count}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Users */}
        <div style={s.section}>
          <div style={s.sectionTitle}>Users</div>
          {users.map(([name, count]) => (
            <div key={name} style={s.userRow}>
              <div style={s.avatar}>{name.charAt(0).toUpperCase()}</div>
              <span style={{ flex: 1, fontSize: 13, color: 'var(--text-primary)' }}>{name}</span>
              <span style={{ fontFamily: 'DM Mono, monospace', fontSize: 11, color: 'var(--accent-soft)' }}>{count} entries</span>
              <div style={{ width: 120, ...s.barTrack }}>
                <div style={s.barFill((count / (users[0]?.[1] || 1)) * 100)} />
              </div>
            </div>
          ))}
        </div>

        {/* Top tags */}
        {topTags.length > 0 && (
          <div style={s.section}>
            <div style={s.sectionTitle}>Top Tags</div>
            <div style={s.tagCloud}>
              {topTags.map(([tag, count]) => (
                <span key={tag} style={s.tag}>{tag} · {count}</span>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
